import Component from '@glimmer/component'
import { service } from '@ember/service'
import { trackedFunction } from 'reactiveweb/function'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type { StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector'
import type SessionService from 'teacher-ui-client/services/session'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import type {
  PaginationPipelineDescriptor,
  SortingPipelineDescriptor,
} from '@blakeelearning/data-tables/utils/data-transformer'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  injectComparerKeys,
  updateSortColumns,
  type SortingConfig,
} from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import type Student from 'teacher-ui-client/models/student'
import type { NormalizedInterval } from 'date-fns'
import { ErrorsUnexpected } from 'teacher-ui-client/components/errors/unexpected'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { RowLoading } from 'teacher-ui-client/components/base/row-loading'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { TimeOnTaskHistoryDataItem } from 'teacher-ui-client/utils/student-events/fetchers/time-on-task-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface ThSortSignature<K extends string> {
  Args: {
    text: string
    sortingConfig: SortingConfig
    sortKey: K
    updateSort: (sortKey: K) => void
  }
}

class ThSort<K extends string> extends Component<ThSortSignature<K>> {
  get sortViewModel() {
    return getSortViewModel(this.args.sortingConfig, this.args.sortKey)
  }

  <template>
    <th data-test-sort-key={{@sortKey}} aria-sort={{this.sortViewModel.ariaDirection}}>
      <button
        type="button"
        class="flex h-full w-full items-center justify-between gap-3 p-3 text-left"
        {{on "click" (fn @updateSort @sortKey)}}
      >
        <span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400" />
      </button>
    </th>
  </template>
}

interface Signature {
  Element: HTMLTableElement
  Args: {
    noContentClass?: string
    statsScope: StudentsScope
    period: NormalizedInterval
    scopedStudents: Student[]
  }
}

interface HistoryStudentViewModel extends Partial<TimeOnTaskHistoryDataItem> {
  id: string
  firstName: string
  lastName: string
  externalId: Nullable<string>
  gradePosition: number
  classesJoined: string
  teachersJoined: string
}

export class TimeOnTaskHistory extends Component<Signature> {
  @service studentEvents!: StudentEventsService

  @service dataTransformer!: DataTransformerService

  @service session!: SessionService

  @queryParam('reporting/ui-scope/writing-legends/time-on-task') sort!: string

  @queryParam('reporting/ui-scope/writing-legends/time-on-task') pageNumber!: number

  @queryParam('reporting/ui-scope/writing-legends/time-on-task') perPage!: number

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Writing)
  }

  /**
   * Fetches the history data for the given scope. Since this already reacts to scope changing (statsScope) we also
   * obtain the scopedStudents and do the mapping here, so that this is the only tracked function that reacts to a scope
   * change.
   */
  fetchHistory = trackedFunction(this, async () => {
    const students = this.args.scopedStudents
    const statsData = await this.studentEvents.timeOnTaskHistory(
      'writing_legends',
      this.args.statsScope,
      this.args.period,
    )
    if (!statsData) return null

    // map to view models
    return students.filter(hasId).map<HistoryStudentViewModel>((student) => {
      const historyStudentItem = statsData.find((s) => String(s.student_id) === student.id)
      return {
        id: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        externalId: student.externalId,
        gradePosition: student.gradePosition,
        classesJoined: student.writingSchoolClassNames.join(', '),
        teachersJoined: student.writingTeacherNames.join(', '),
        ...historyStudentItem,
      }
    })
  })

  @use
  latestFetch = keepLatest({
    value: () => this.fetchHistory.value,
    when: () => this.fetchHistory.isPending,
  })

  get sortingConfig(): SortingConfig {
    const sortingConfig = deserializeSort(this.sort)
    return injectComparerKeys(sortingConfig, {
      // sorting defaults to 'sortByValue' comparer, place alternative comparers for any columns here.
      // total_seconds: 'sortByNumber',
    })
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  get sortingDescriptor(): SortingPipelineDescriptor {
    return { type: 'sort', config: this.sortingConfig }
  }

  updateSort = (sortKey: string) => {
    this.sortingConfig = updateSortColumns(this.sortingConfig, sortKey)
  }

  get paginationConfig(): PaginationConfig {
    return { pageNumber: Number(this.pageNumber), perPage: Number(this.perPage) }
  }

  get paginationDescriptor(): PaginationPipelineDescriptor {
    return { type: 'paginate', config: this.paginationConfig }
  }

  /**
   * A tracked function to transform the history data. We only want this function to run when the history data changes
   * from fetchHistory, or the pipeline changes (eg sort qp changed).
   */
  transformHistory = trackedFunction(this, async () => {
    const items = this.latestFetch ?? []
    const pipeline = [this.sortingDescriptor, this.paginationDescriptor]
    return this.dataTransformer.buildAndTransform<HistoryStudentViewModel, HistoryStudentViewModel>(items, pipeline)
  })

  @use
  latestTransform = keepLatest({
    value: () => this.transformHistory.value,
    when: () => this.transformHistory.isPending,
  })

  get hasData() {
    return this.latestFetch && this.latestTransform
  }

  get isLoading() {
    return this.fetchHistory.isPending || this.transformHistory.isPending
  }

  get isError() {
    return this.fetchHistory.isError || this.transformHistory.isError
  }

  <template>
    {{#if this.hasData}}
      <table
        class="divide-dusty-black-200 border-dusty-black-200 h-fit w-full table-fixed divide-y border-b
          {{if this.isLoading 'opacity-50'}}"
        data-test-history-table
        ...attributes
      >
        <thead>
          <tr>
            <ThSort
              @sortingConfig={{this.sortingConfig}}
              @updateSort={{this.updateSort}}
              @text={{t "tables.timeOnTask.headers.firstName"}}
              @sortKey="firstName"
            />
            <ThSort
              @sortingConfig={{this.sortingConfig}}
              @updateSort={{this.updateSort}}
              @text={{t "tables.timeOnTask.headers.lastName"}}
              @sortKey="lastName"
            />
            <ThSort
              @sortingConfig={{this.sortingConfig}}
              @updateSort={{this.updateSort}}
              @text={{t "tables.timeOnTask.headers.class"}}
              @sortKey="classesJoined"
            />
            {{#if this.isSubco}}
              <ThSort
                @sortingConfig={{this.sortingConfig}}
                @updateSort={{this.updateSort}}
                @text={{t "tables.timeOnTask.headers.teacherName"}}
                @sortKey="teachersJoined"
              />
              <ThSort
                @sortingConfig={{this.sortingConfig}}
                @updateSort={{this.updateSort}}
                @text={{t "grade.label"}}
                @sortKey="gradePosition"
              />
            {{/if}}
            <ThSort
              @sortingConfig={{this.sortingConfig}}
              @updateSort={{this.updateSort}}
              @text={{t "tables.timeOnTask.headers.totalHours"}}
              @sortKey="total_seconds"
            />
            <ThSort
              @sortingConfig={{this.sortingConfig}}
              @updateSort={{this.updateSort}}
              @text={{t "tables.timeOnTask.headers.avgHoursByWeek" htmlSafe=true}}
              @sortKey="average_seconds_per_week"
            />
            <ThSort
              @sortingConfig={{this.sortingConfig}}
              @updateSort={{this.updateSort}}
              @text={{t "tables.timeOnTask.headers.totalSessions" htmlSafe=true}}
              @sortKey="session_count"
            />
            <ThSort
              @sortingConfig={{this.sortingConfig}}
              @updateSort={{this.updateSort}}
              @text={{t "tables.timeOnTask.headers.avgTimeInSession" htmlSafe=true}}
              @sortKey="average_seconds_per_session"
            />
          </tr>
        </thead>
        <tbody class="divide-dusty-black-200 relative divide-y">
          <RowLoading class="p-3" @show={{this.isLoading}} @absolute={{true}} />
          {{#each this.latestTransform.items as |historyStudentItem|}}
            <tr data-test-row={{historyStudentItem.id}}>
              <td class="p-3 wrap-break-word" data-test-cell="first-name">{{formatValue
                  historyStudentItem.firstName
                }}</td>
              <td class="p-3 wrap-break-word" data-test-cell="last-name">{{formatValue
                  historyStudentItem.lastName
                }}</td>
              <td class="p-3 wrap-break-word" data-test-cell="class-names">
                {{formatValue historyStudentItem.classesJoined}}
              </td>
              {{#if this.isSubco}}
                <td class="p-3 wrap-break-word" data-test-cell="teacher-names">
                  {{formatValue historyStudentItem.teachersJoined}}
                </td>
                <td class="p-3" data-test-cell="grade-position">{{gradeName historyStudentItem.gradePosition}}</td>
              {{/if}}
              <td class="p-3" data-test-cell="total-seconds">{{formatValue
                  (formatDuration historyStudentItem.total_seconds)
                }}</td>
              <td class="p-3" data-test-cell="avg-per-week">{{formatValue
                  (formatDuration historyStudentItem.average_seconds_per_week)
                }}</td>
              <td class="p-3" data-test-cell="session-count">{{formatValue historyStudentItem.session_count}}</td>
              <td class="p-3" data-test-cell="avg-per-session">{{formatValue
                  (formatDuration historyStudentItem.average_seconds_per_session)
                }}</td>
            </tr>
          {{/each}}
        </tbody>
      </table>
      <BasePageSelector
        class="my-3"
        @pageCount={{this.latestTransform.pageCount}}
        @itemCount={{this.latestFetch.length}}
      />
      {{! Table for export }}
      <div class="hidden">
        <table exportable>
          <thead>
            <tr>
              <th>{{t "tables.timeOnTask.headers.firstName"}}</th>
              <th>{{t "tables.timeOnTask.headers.lastName"}}</th>
              <th>{{t "reporting.dashboardTables.studentExternalId"}}</th>
              <th>{{t "tables.timeOnTask.headers.class"}}</th>
              {{#if this.isSubco}}
                <th>{{t "tables.timeOnTask.headers.teacherName"}}</th>
                <th>{{t "grade.label"}}</th>
              {{/if}}
              <th>{{t "tables.timeOnTask.headers.totalHours"}}</th>
              <th>{{t "tables.timeOnTask.headers.avgHoursByWeek" htmlSafe=true}}</th>
              <th>{{t "tables.timeOnTask.headers.totalSessions" htmlSafe=true}}</th>
              <th>{{t "tables.timeOnTask.headers.avgTimeInSession" htmlSafe=true}}</th>
            </tr>
          </thead>
          <tbody>
            {{#each this.latestTransform.paginated as |page|}}
              {{#each page as |historyStudentItem|}}
                <tr data-test-row={{historyStudentItem.id}}>
                  <td data-test-cell="first-name">{{formatValue historyStudentItem.firstName}}</td>
                  <td data-test-cell="last-name">{{formatValue historyStudentItem.lastName}}</td>
                  <td data-test-cell="external-id">{{formatValue historyStudentItem.externalId}}</td>
                  <td data-test-cell="class-names">{{formatValue historyStudentItem.classesJoined}}</td>
                  {{#if this.isSubco}}
                    <td data-test-cell="teacher-names">{{formatValue historyStudentItem.teachersJoined}}</td>
                    <td data-test-cell="grade-position">{{gradeName historyStudentItem.gradePosition}}</td>
                  {{/if}}
                  <td data-test-cell="total-seconds">{{formatValue
                      (formatDuration historyStudentItem.total_seconds)
                    }}</td>
                  <td data-test-cell="avg-per-week">{{formatValue
                      (formatDuration historyStudentItem.average_seconds_per_week)
                    }}</td>
                  <td data-test-cell="session-count">{{formatValue historyStudentItem.session_count}}</td>
                  <td data-test-cell="avg-per-session">{{formatValue
                      (formatDuration historyStudentItem.average_seconds_per_session)
                    }}</td>
                </tr>
              {{/each}}
            {{/each}}
          </tbody>
        </table>
      </div>
    {{else if this.isLoading}}
      {{! initial load }}
      <div class={{@noContentClass}} data-test-history-loading><FidgetSpinnerWaveComponent @centered={{true}} /></div>
    {{else if this.isError}}
      <ErrorsUnexpected @compact={{true}} class={{@noContentClass}} data-test-history-error />
    {{/if}}
  </template>
}
